import { withTransaction } from '@elastic/apm-rum-react'
import { Campaign, Checkout, Gamification, LexLuthorService, LogService, Prizes, Ranking, User, UserInDetails } from '@hub/shared'
import { SqLoader } from '@squidit/react-css'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router'
import EngageHeader from './components/engage-header/engage-header.component'
import './engage.page.scoped.scss'
import CheckoutsPage from './pages/checkouts/checkouts.page'
import PointingPage from './pages/pointing/pointing.page'
import PrizesPage from './pages/prizes/prizes.page'
import RankingPage from './pages/ranking/ranking.page'
import RulesPage from './pages/rules/rules.page'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  campaign: Campaign
  userInCampaign: UserInDetails
  user: User
}

const EngagePageComponent = ({ campaign, userInCampaign, user }: Props) => {
  const { pathname } = useLocation()
  const [loading, setLoading] = useState(true)
  const [userRanking, setUserRanking] = useState<Ranking>(null)
  const [userRankingLast, setUserRankingLast] = useState<Ranking>(null)
  const [gamifications, setGamifications] = useState<Gamification[]>([])
  const [prizes, setPrizes] = useState<Prizes[]>([])
  const logService = useMemo(() => new LogService(), [])
  const lexLuthorService = useMemo(() => new LexLuthorService(), [])
  const [checkouts, setCheckouts] = useState<Checkout[]>([])
  logService.init('Engage Details Page')

  useEffect(() => {
    Promise.all([
      lexLuthorService.getGamifications(campaign._id).then((gamifications) => {
        setGamifications(gamifications)
      }),
      lexLuthorService.getRankingsHistory(campaign._id, userInCampaign.idNetwork).then((score) => {
        if (score?.length > 1) {
          setUserRankingLast(score[score?.length - 2])
        }
        setUserRanking(score[score?.length - 1])
      }),
    ]).finally(() => setLoading(false))
  }, [campaign?._id, userInCampaign?.idNetwork, lexLuthorService])

  const onConfirmCheckout = useCallback(() => {
    setLoading(true)
    Promise.all([
      lexLuthorService.getRankingsHistory(campaign._id, userInCampaign.idNetwork).then((score) => {
        if (score?.length > 1) {
          setUserRankingLast(score[score?.length - 2])
        }
        setUserRanking(score[score?.length - 1])
      }),
    ]).finally(() => setLoading(false))
  }, [campaign, userInCampaign, lexLuthorService])

  if (!campaign?.activation?.hasGamification) {
    return <Navigate to={`${pathname?.replace('engage', 'orientation')}`} />
  }

  if (loading) {
    return (
      <div
        className="text-center my-5 display-flex align-items-center justify-content-center"
        style={{ width: '100%', height: 'calc(100% - 4rem)' }}
      >
        <SqLoader size="big" />
      </div>
    )
  }

  return (
    <section className="squid-engage-page">
      <EngageHeader
        userInCampaign={userInCampaign}
        path={pathname.replace(`/${pathname.split('/')[6]}`, '')}
        userRanking={userRanking}
        userRankingLast={userRankingLast}
        prizesCount={prizes?.length}
        campaign={campaign}
      />
      <div className="engage-content">
        <Routes>
          <Route path={'/'} element={<Navigate to={`${pathname}/pointing`} />} />
          <Route path={'/ranking'} element={<RankingPage campaign={campaign} userRanking={userRanking} />} />
          <Route
            path={'/pointing'}
            element={<PointingPage campaign={campaign} userRanking={userRanking} gamifications={gamifications} />}
          />
          <Route path={'/rules'} element={<RulesPage campaign={campaign} gamifications={gamifications} />} />
          <Route
            path={'/prizes'}
            element={
              <PrizesPage
                onConfirmCheckout={onConfirmCheckout}
                checkouts={checkouts}
                campaign={campaign}
                prizes={prizes}
                userRanking={userRanking}
                pathname={pathname}
              />
            }
          />
          <Route path={'/checkouts'} element={<CheckoutsPage checkouts={checkouts} prizes={prizes} pathname={pathname} />} />
          <Route element={<Navigate to="/error/not-found" />} />
        </Routes>
      </div>
    </section>
  )
}
export default withTransaction('EngagePageComponent', 'component')(EngagePageComponent)
